import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const STATUS_LABELS = {
  early: { label: 'Antecipada', color: 'bg-green-100 text-green-800' },
  on_time: { label: 'No Prazo', color: 'bg-blue-100 text-blue-800' },
  late_minor: { label: 'Atraso <=3d', color: 'bg-yellow-100 text-yellow-800' },
  late_major: { label: 'Atraso >3d', color: 'bg-red-100 text-red-800' }
};

function Performance({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dashboard, setDashboard] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [rankType, setRankType] = useState('monthly');
  const [consultantDetail, setConsultantDetail] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const isAdmin = user?.role === 'ADMIN';

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get(`/performance/dashboard?month=${month}`);
      setDashboard(res.data);
    } catch (err) {
      toast.error('Erro ao carregar dashboard');
    }
  }, [month]);

  const fetchRankings = useCallback(async () => {
    try {
      let url = `/performance/rankings?type=${rankType}&month=${month}`;
      if (rankType === 'project' && selectedProjectId) {
        url += `&project_id=${selectedProjectId}`;
      }
      const res = await api.get(url);
      setRankings(res.data);
    } catch (err) {
      toast.error('Erro ao carregar rankings');
    }
  }, [rankType, month, selectedProjectId]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchDashboard(), fetchRankings()]);
      if (isAdmin) {
        const [settRes, projRes] = await Promise.all([
          api.get('/performance/settings'),
          api.get('/projects').catch(() => ({ data: [] }))
        ]);
        setSettings(settRes.data);
        setProjects(projRes.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados de Desempenho:', err);
      toast.error('Erro ao carregar dados de Desempenho');
    } finally {
      setLoading(false);
    }
  }, [fetchDashboard, fetchRankings, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => { if (!loading) fetchRankings(); }, [rankType, selectedProjectId, fetchRankings, loading]);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await api.post('/performance/recalculate');
      toast.success(`${res.data.total} scores recalculados`);
      fetchAll();
    } catch (err) {
      toast.error('Erro ao recalcular');
    } finally {
      setRecalculating(false);
    }
  };

  const saveSettings = async () => {
    try {
      await api.put('/performance/settings', settings);
      toast.success('Configurações salvas');
      setShowSettings(false);
      fetchAll();
    } catch (err) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const viewConsultant = async (consultantId) => {
    try {
      const res = await api.get(`/performance/consultant/${consultantId}`);
      setConsultantDetail(res.data);
    } catch (err) {
      toast.error('Erro ao carregar dados do consultor');
    }
  };

  // Bar chart renderer (pure CSS)
  const BarChart = ({ data, maxVal, label, color = 'bg-emerald-500' }) => (
    <div className="space-y-1.5">
      {data.map((item, i) => {
        const pct = maxVal > 0 ? (item.value / maxVal) * 100 : 0;
        return (
          <div key={`bar-${item.label}-${i}`} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-20 text-right truncate">{item.label}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
              <div className={`${color} h-full rounded-full transition-all flex items-center justify-end pr-2`} style={{ width: `${Math.max(pct, 6)}%` }}>
                {item.value > 0 && <span className="text-xs text-white font-bold">{item.value}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="performance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="performance-title">
              Desempenho
            </h1>
            <p className="text-gray-500 mt-1">Gamificação e produtividade dos consultores</p>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              className="w-40"
              data-testid="month-selector"
            />
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="btn-settings">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Config
                </Button>
                <Button variant="outline" size="sm" onClick={handleRecalculate} disabled={recalculating} data-testid="btn-recalculate">
                  {recalculating ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div> : 'Recalcular'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Gamification disabled warning */}
        {settings && !settings.enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="gamification-disabled">
            Gamificação desativada. Ative nas configurações para calcular pontuações.
          </div>
        )}

        {/* KPI Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="performance-kpis">
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-total-score">{dashboard.total_score}</p>
                  <p className="text-xs text-gray-500">Score Total</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-tasks-completed">{dashboard.tasks_completed}</p>
                  <p className="text-xs text-gray-500">Tarefas Concluídas</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-ontime-rate">{dashboard.on_time_rate}%</p>
                  <p className="text-xs text-gray-500">Taxa no Prazo</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900" data-testid="kpi-late-tasks">{dashboard.late_minor_count + dashboard.late_major_count}</p>
                  <p className="text-xs text-gray-500">Tarefas Atrasadas</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="performance-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="ranking" data-testid="tab-ranking">Ranking</TabsTrigger>
            {isAdmin && <TabsTrigger value="detalhes" data-testid="tab-detalhes">Detalhes</TabsTrigger>}
          </TabsList>

          {/* === DASHBOARD TAB === */}
          <TabsContent value="dashboard" className="mt-4 space-y-6">
            {dashboard && (
              <>
                {/* Evolution chart */}
                <div className="bg-white rounded-xl border p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Evolução de Score</h3>
                  <div className="flex items-end gap-2 h-40">
                    {dashboard.evolution.map((e, i) => {
                      const maxScore = Math.max(...dashboard.evolution.map(ev => ev.score), 1);
                      const h = (e.score / maxScore) * 100;
                      return (
                        <div key={`evo-${e.month}`} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs font-bold text-gray-700">{e.score > 0 ? e.score : ''}</span>
                          <div className="w-full bg-gray-100 rounded-t-lg flex items-end" style={{ height: '120px' }}>
                            <div
                              className="w-full bg-emerald-500 rounded-t-lg transition-all"
                              style={{ height: `${Math.max(h, 2)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400">{e.month.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Completion breakdown */}
                <div className="bg-white rounded-xl border p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Performance por Prazo</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{dashboard.early_count}</p>
                      <p className="text-xs text-green-600">Antecipadas</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{dashboard.on_time_count}</p>
                      <p className="text-xs text-blue-600">No Prazo</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-700">{dashboard.late_minor_count}</p>
                      <p className="text-xs text-yellow-600">Atraso 1-3d</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-700">{dashboard.late_major_count}</p>
                      <p className="text-xs text-red-600">Atraso &gt;3d</p>
                    </div>
                  </div>
                </div>

                {/* Recent scores */}
                {dashboard.recent_scores.length > 0 && (
                  <div className="bg-white rounded-xl border p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Últimas Pontuações</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="recent-scores-table">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Tarefa</th>
                            {isAdmin && <th className="py-2 px-3 text-left font-medium text-gray-600">Consultor</th>}
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Projeto</th>
                            <th className="py-2 px-3 text-center font-medium text-gray-600">Status</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Peso</th>
                            <th className="py-2 px-3 text-right font-medium text-gray-600">Score</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dashboard.recent_scores.map((s, i) => {
                            const st = STATUS_LABELS[s.status_label] || { label: s.status_label, color: 'bg-gray-100 text-gray-700' };
                            return (
                              <tr key={s.id || `recent-${i}-${s.task_name}`} className="hover:bg-gray-50">
                                <td className="py-2 px-3 font-medium">{s.task_name}</td>
                                {isAdmin && <td className="py-2 px-3 text-gray-600">{s.consultant_name}</td>}
                                <td className="py-2 px-3 text-gray-500 text-xs">{s.project_name}</td>
                                <td className="py-2 px-3 text-center">
                                  <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                                </td>
                                <td className="py-2 px-3 text-right font-mono text-gray-500">{s.task_weight}</td>
                                <td className="py-2 px-3 text-right font-bold font-mono text-emerald-700">
                                  {s.final_score}
                                  {s.rework_penalty && <span className="text-xs text-red-500 ml-1">-20%</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* === RANKING TAB === */}
          <TabsContent value="ranking" className="mt-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Select value={rankType} onValueChange={setRankType}>
                <SelectTrigger className="w-48" data-testid="select-rank-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Ranking Mensal</SelectItem>
                  <SelectItem value="project">Ranking por Projeto</SelectItem>
                  <SelectItem value="general">Ranking Geral</SelectItem>
                </SelectContent>
              </Select>
              {rankType === 'project' && (
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-64"><SelectValue placeholder="Selecione o projeto" /></SelectTrigger>
                  <SelectContent>
                    {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {rankings && rankings.rankings.length > 0 ? (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rankings-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="py-3 px-4 text-center font-medium text-gray-600 w-12">#</th>
                        <th className="py-3 px-4 text-left font-medium text-gray-600">Consultor</th>
                        <th className="py-3 px-4 text-right font-medium text-gray-600">Score</th>
                        <th className="py-3 px-4 text-right font-medium text-gray-600">Tarefas</th>
                        <th className="py-3 px-4 text-right font-medium text-gray-600">No Prazo</th>
                        <th className="py-3 px-4 text-right font-medium text-gray-600">Atrasadas</th>
                        <th className="py-3 px-4 text-right font-medium text-gray-600">Taxa %</th>
                        {isAdmin && <th className="py-3 px-4 text-center font-medium text-gray-600">Ações</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {rankings.rankings.map((r) => (
                        <tr key={r.consultant_id} className={`hover:bg-gray-50 ${r.consultant_id === user?.user_id ? 'bg-emerald-50' : ''}`} data-testid={`rank-row-${r.rank}`}>
                          <td className="py-3 px-4 text-center">
                            {r.rank <= 3 ? (
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
                                r.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                                r.rank === 2 ? 'bg-gray-300 text-gray-800' :
                                'bg-amber-600 text-white'
                              }`}>{r.rank}</span>
                            ) : (
                              <span className="text-gray-500 font-mono">{r.rank}</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-900">{r.consultant_name}</p>
                          </td>
                          <td className="py-3 px-4 text-right font-bold font-mono text-emerald-700">{r.total_score}</td>
                          <td className="py-3 px-4 text-right font-mono">{r.tasks_completed}</td>
                          <td className="py-3 px-4 text-right font-mono text-green-600">{r.early_count + r.on_time_count}</td>
                          <td className="py-3 px-4 text-right font-mono text-red-600">{r.late_count}</td>
                          <td className="py-3 px-4 text-right">
                            <Badge className={`text-xs ${r.on_time_rate >= 80 ? 'bg-green-100 text-green-800' : r.on_time_rate >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {r.on_time_rate}%
                            </Badge>
                          </td>
                          {isAdmin && (
                            <td className="py-3 px-4 text-center">
                              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => viewConsultant(r.consultant_id)}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl border">
                <p className="text-gray-500 text-lg">Sem dados para o ranking</p>
                <p className="text-gray-400 text-sm mt-1">Complete tarefas para gerar pontuações</p>
              </div>
            )}

            {/* My rank (for consultants) */}
            {!isAdmin && rankings?.my_rank && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <h3 className="font-semibold text-emerald-900 mb-2">Sua Posição</h3>
                <div className="flex items-center gap-4">
                  <span className="text-3xl font-bold text-emerald-700">#{rankings.my_rank.rank}</span>
                  <div>
                    <p className="text-sm text-emerald-800">Score: <strong>{rankings.my_rank.total_score}</strong></p>
                    <p className="text-sm text-emerald-700">{rankings.my_rank.tasks_completed} tarefas | {rankings.my_rank.on_time_rate}% no prazo</p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* === DETALHES TAB (Admin) === */}
          {isAdmin && (
            <TabsContent value="detalhes" className="mt-4 space-y-4">
              <h3 className="font-semibold text-gray-700">Selecione um consultor do ranking para ver detalhes</h3>
              {consultantDetail ? (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{consultantDetail.consultant?.full_name}</h3>
                        <p className="text-sm text-gray-500">{consultantDetail.consultant?.email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setConsultantDetail(null)}>Fechar</Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-emerald-50 rounded-lg">
                        <p className="text-xl font-bold text-emerald-700">{consultantDetail.total_score}</p>
                        <p className="text-xs text-gray-500">Score Total</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-xl font-bold text-blue-700">{consultantDetail.tasks_completed}</p>
                        <p className="text-xs text-gray-500">Tarefas</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-xl font-bold text-green-700">{consultantDetail.on_time_rate}%</p>
                        <p className="text-xs text-gray-500">No Prazo</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-xl font-bold text-red-700">{consultantDetail.late_count}</p>
                        <p className="text-xs text-gray-500">Atrasadas</p>
                      </div>
                    </div>
                  </div>

                  {/* Monthly history */}
                  {consultantDetail.monthly_history?.length > 0 && (
                    <div className="bg-white rounded-xl border p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Histórico Mensal</h4>
                      <BarChart
                        data={consultantDetail.monthly_history.map(m => ({ label: m.month, value: m.score }))}
                        maxVal={Math.max(...consultantDetail.monthly_history.map(m => m.score))}
                      />
                    </div>
                  )}

                  {/* Project scores */}
                  {consultantDetail.project_scores?.length > 0 && (
                    <div className="bg-white rounded-xl border p-5">
                      <h4 className="font-semibold text-gray-900 mb-3">Score por Projeto</h4>
                      <BarChart
                        data={consultantDetail.project_scores.map(p => ({ label: p.project_name, value: p.score }))}
                        maxVal={Math.max(...consultantDetail.project_scores.map(p => p.score))}
                        color="bg-blue-500"
                      />
                    </div>
                  )}

                  {/* Recent scores */}
                  <div className="bg-white rounded-xl border p-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Últimas Pontuações</h4>
                    <div className="space-y-2">
                      {consultantDetail.recent_scores?.slice(0, 15).map((s, i) => {
                        const st = STATUS_LABELS[s.status_label] || { label: s.status_label, color: 'bg-gray-100' };
                        return (
                          <div key={s.id || `cd-${i}-${s.task_name}`} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                            <div className="flex-1">
                              <span className="font-medium">{s.task_name}</span>
                              <span className="text-gray-400 text-xs ml-2">{s.project_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                              <span className="font-mono font-bold text-emerald-700 w-12 text-right">{s.final_score}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border">
                  <p className="text-gray-500">Clique no ícone de visualizar na tabela do ranking para ver detalhes de um consultor</p>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* === SETTINGS MODAL === */}
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configurações da Gamificação</DialogTitle>
              <DialogDescription>Ajuste as regras de pontuação</DialogDescription>
            </DialogHeader>
            {settings && (
              <div className="grid gap-4 py-2">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Gamificação Ativa</Label>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={v => setSettings({...settings, enabled: v})}
                    data-testid="toggle-gamification"
                  />
                </div>
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Fatores de Performance</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Antecipada (x)</Label>
                      <Input type="number" step="0.1" value={settings.early_factor} onChange={e => setSettings({...settings, early_factor: parseFloat(e.target.value) || 0})} data-testid="input-early-factor" />
                    </div>
                    <div>
                      <Label className="text-xs">No Prazo (x)</Label>
                      <Input type="number" step="0.1" value={settings.on_time_factor} onChange={e => setSettings({...settings, on_time_factor: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label className="text-xs">{"Atraso <=3d (x)"}</Label>
                      <Input type="number" step="0.1" value={settings.late_3days_factor} onChange={e => setSettings({...settings, late_3days_factor: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label className="text-xs">Atraso &gt;3d (x)</Label>
                      <Input type="number" step="0.1" value={settings.late_over3days_factor} onChange={e => setSettings({...settings, late_over3days_factor: parseFloat(e.target.value) || 0})} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Penalidade Retrabalho (%)</Label>
                  <Input type="number" step="0.05" value={settings.rework_penalty} onChange={e => setSettings({...settings, rework_penalty: parseFloat(e.target.value) || 0})} />
                </div>
                <div className="border-t pt-4">
                  <Label className="text-xs">Valor por Ponto (R$) - opcional</Label>
                  <Input type="number" step="0.01" value={settings.point_value_brl || ''} onChange={e => setSettings({...settings, point_value_brl: e.target.value ? parseFloat(e.target.value) : null})} placeholder="Ex: 2.50" data-testid="input-point-value" />
                  {settings.point_value_brl && dashboard && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Bônus estimado do mês: R$ {(dashboard.total_score * settings.point_value_brl).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancelar</Button>
              <Button onClick={saveSettings} className="bg-emerald-700 hover:bg-emerald-800" data-testid="btn-save-settings">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default Performance;
